
import React, {useState, useEffect} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components'
import { useTable, useSortBy, useGlobalFilter, usePagination, useAsyncDebounce, useRowState } from 'react-table'
import * as Format from "../Format";
import Badge from "../Badge";
import FormGroup from "../forms/FormGroup";
import FilterButton from "../FilterButton";
import capitalize from "lodash/capitalize";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons'

export const StyledTable = styled.table`
  border-bottom: 1px solid #ddd !important;
  height: auto !important;
  max-height: 700px !important;
  clear: both;
  max-width: none !important;
  border-collapse: separate !important;
  border-spacing: 0;
  width: 100%;
  tr {
    height: ${props => props.small ? "46" : "60"}px;
    & > *:first-child {
      padding-left: ${props => props.small ? "35" : "50"}px;
      padding-left: ${props => props.noPadding ? "8px" : "initial"};
    }
    & > *:last-child {
      padding-right: ${props => props.small ? "35" : "50"}px;
      padding-right: ${props => props.noPadding ? "8px" : "initial"};
    }
  }
  thead th {
    padding: 0 8px;
    padding-right: 30px;
    border-top: 0;
    font-size: 13px;
    font-weight: 600;
    border-bottom: 1px solid #FF513C;
    background: #DEDEDE;
    background: white;
  }

  tbody tr {
    td {
      padding: 0 8px;
      vertical-align: middle !important;
      /*border-top: 1px solid #FED3CF;*/
      border-top: 1px solid #DDDDDD;
      &:first-of-type{
        text-transform: uppercase;
        font-weight: 600;
      }
    }
    &:nth-child(odd) > td {
      background-color: #FAFAFA;
    }
    &:hover {
      cursor: ${props => props.clickable ? "pointer" : "inherit"};
      td {
        ${props => (props.clickable || props.hoverable) ? "background-color: #f5f5f5 !important;" : ""}
      }
    }
  }
`;

function SortIcons({asc, desc}) {
  const color = (sorted) => sorted ? '#000' : '#999'

  const ascIcon = (sorted) => <span style={{color: color(sorted), marginRight: '-2px'}}>{'\u2193'}</span>
  const descIcon = (sorted) =><span style={{color: color(sorted)}}>{'\u2191'}</span>

  return (
    <>
      {ascIcon(asc)}
      {descIcon(desc)}
    </>
  )
}

export function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
	const { t, i18n } = useTranslation();
  const count = preGlobalFilteredRows.length
  const [value, setValue] = useState(globalFilter)
  const onChange = useAsyncDebounce(value => {
    setGlobalFilter(value || undefined)
  }, 200)

  return (
  	<div className="col-sm-12 col-md-6">
  		<div className="dataTables_filter">
  			<label>
	  			{t('table.search')}&nbsp;:
	  			<input
	  				type="search"
	  				className="form-control form-control-sm"
	  				placeholder=""
	  				value={value || ""}
	  				onChange={e => {
		          setValue(e.target.value);
		          onChange(e.target.value);
		        }}
		       />
  			</label>
  		</div>
  	</div>
  )
}

function PaginationItem(props) {
	return (
		<li className={`page-item ${props.disabled && 'disabled'}`}>
			<a className="page-link" onClick={props.onClick}>
				{props.children}
			</a>
		</li>
	)
}

export function Pagination({
	pageIndex,
	pageSize,
	pageCount,
	totalSize,
	canNextPage,
	canPreviousPage,
	nextPage,
	previousPage,
	gotoPage
}) {
	const { t } = useTranslation();

	const pages = [1, ...(pageIndex <= 3 ? [2, 3, 4, 5] : []), pageCount, pageIndex - 1, pageIndex, pageIndex + 1].sort((a,b) =>
		a - b
	).filter(x =>
		(x>=1 && x<=pageCount)
	).filter((value, index, self) =>
		(self.indexOf(value)===index)
	)

	return (
		<div className="row">
			<div className="col-sm-12 col-md-5">
				<div className="dataTables_info">
					{t('table.elementsCount', { firstElement: (pageIndex - 1) * pageSize + 1, lastElement: Math.min(pageIndex * pageSize, totalSize) , totalElements: totalSize})}
				</div>
			</div>
			<div className="col-sm-12 col-md-7">
				<div className="dataTables_paginate paging_full_numbers">
					<ul className="pagination">
						<PaginationItem disabled={!canPreviousPage} onClick={() => previousPage()} key='prev'>
              <FontAwesomeIcon icon={faAngleLeft} />
						</PaginationItem>
						{
							pages.map((page, i) => (
								<React.Fragment key={page}>
									{i > 0 && pages[i]-1 - pages[i-1] > 1 && <PaginationItem disabled={true} key={page-1}>
										…
									</PaginationItem>}
									{i > 0 && pages[i]-1 - pages[i-1] == 1 && <PaginationItem disabled={false} key={page-1}>
										{page - 1}
									</PaginationItem>}
									<PaginationItem disabled={page == pageIndex} onClick={() => gotoPage(page - 1)} key={page}>
										{page}
									</PaginationItem>
								</React.Fragment>
								)
							)
						}
						<PaginationItem disabled={!canNextPage} onClick={() => nextPage()} key='next'>
              <FontAwesomeIcon icon={faAngleRight} />
						</PaginationItem>
					</ul>
				</div>
			</div>
		</div>
	)
}

export function TableHead({headerGroups, autoWidth}) {
  return (
    <thead>
    {headerGroups.map(headerGroup => (
      <tr {...headerGroup.getHeaderGroupProps()}>
        {headerGroup.headers.map(column => (
          <th {...column.getHeaderProps(column.getSortByToggleProps({style: {...getStyle(column, autoWidth), ...column.headerStyle}}))}>
            {column.render('Header')}
            { column.canSort &&
            <span style={{float: 'right'}}>
              <SortIcons asc={column.isSorted && !column.isSortedDesc} desc={column.isSortedDesc}/>
            </span>
            }
            <div>{column.canFilter ? column.render('Filter') : null}</div>
          </th>
        ))}
      </tr>
    ))}
    </thead>
  )
}

export function TableBody({tableBodyProps, paginate, prepareRow, page, rows, onRowClick, onRowHover, autoWidth, getRowProps = () => ({})}) {
  return (
    <tbody {...tableBodyProps}>
    {(paginate ? page : rows).map((row, i) => {
      prepareRow(row)
      return (
        <tr {...row.getRowProps({
          onClick: e => onRowClick && onRowClick(row.original),
          onMouseEnter: e => onRowHover && onRowHover(row.original),
          onMouseLeave: e => onRowHover && onRowHover(null),
          className: row.state.highlighted ? "highlighted": "",
          ...getRowProps(row)
        })} >
          {row.cells.map(cell => {
            return (
              <td {...cell.getCellProps({style: getStyle(cell.column, autoWidth)})}>
                { cell.render('Cell')}
              </td>
            )
          })}
        </tr>
      )
    })}
    </tbody>
  )
}


function getStyle(obj, autoWidth) {
  let style = {}
  if (!autoWidth) {
    if (obj.minWidth) {
      style = {...style, minWidth: obj.minWidth+'px'}
    }
    if (obj.maxWidth) {
      style = {...style, maxWidth: obj.maxWidth+'px'}
    }
    if (obj.width) {
      style = {...style, width: obj.width+'px'}
    }
  }
  return style
}

function FormattedCell({formatOptions = {}, ...props}) {
  let format = props.format;
  if (!format) {
    format = typeof(props.value);
  }

  if (format === 'date') {
    return <Format.Date date={props.value} dateFormat={formatOptions.dateFormat}/>
  } else if (format === 'number') {
    return <Format.Number value={props.value} digits={formatOptions.digits}/>
  } else if (format === 'price') {
    return <Format.Price value={props.value} digits={formatOptions.digits}/>
  } else if (format === 'badge') {
    return <Badge color={formatOptions.color}>{props.value}</Badge>
  } else if (format === 'capitalize') {
    return <>{capitalize(props.value)}</>
  } else {
    return <>{props.value}</>
  }
}

export function formattedColumns(cols) {
  return cols.map(col => {
    const {format, formatOptions, filter, sortType, ...colOptions} = col
    let sortOptions = {}
    let filter_options = {}
    if (filter === 'select') {
      filter_options = {
        ...filter_options,
        Filter: ColumnSelectFilter
      }
    } else if (filter) {
      filter_options = {filter}
    }
    if (sortType == 'caseInsensitiveString') {
      sortOptions = {sortType: (rowA, rowB, id, desc) => (rowA.original[id].localeCompare(rowB.original[id], undefined, {sensitivity: 'base'}))}
    } else if (sortType != null) {
      sortOptions = {sortType}
    }
    return {
      Cell: (cell) => <FormattedCell format={format} value={cell.value} formatOptions={formatOptions} />,
      ...colOptions,
      ...filter_options,
      ...sortOptions
    }
  })
}

export function DefaultColumnFilter({column: { id, filterValue, setFilter }}) {
  const [value, setValue] = useState('')

  useEffect(() => {
    setValue(filterValue || '')
  }, [filterValue])

  const debouncedSetFilter = useAsyncDebounce(val => {
    debugger
    setFilter(val)
  }, 200)

  const onChange = (e) => {
    const val = e.target.value || undefined
    setValue(val || '')
    debouncedSetFilter(val)
  }

  return (
    <FormGroup
      input="text"
      name={id}
      value={value}
      onChange={onChange}
      placeholder={`Search...`}
      style={{marginTop: '1rem'}}
      onClick={(e) => {
        e.stopPropagation();
      }}
    />
  )
}

function ColumnSelectFilter({setFilter, column: {id, options, filter_searchable = false}}) {
  return (
    <FilterButton
      options={options}
      search={filter_searchable}
      includeAll={true}
      setFilter={setFilter}
      name={id}
      variant={"input"}
      maxElements={1}/>
  )
}
